import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  MessageCircle,
  Mail,
  Sparkles,
  Bell,
  Heart,
  Instagram,
  Youtube,
  Facebook,
  Send
} from 'lucide-react';
import Navigation from '../components/Navigation';

const StayConnectedPage = () => {
  const [newsletterData, setNewsletterData] = useState({
    name: '',
    email: '',
    interests: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Newsletter signup:', newsletterData);
  };

  const benefits = [
    {
      icon: Sparkles,
      title: 'Exclusive Content',
      description: 'Behind-the-scenes stories, speaker interviews, and early announcements'
    },
    {
      icon: Bell,
      title: 'Event Updates',
      description: 'Be first to know about schedule changes, new sessions, and surprises'
    },
    {
      icon: Heart,
      title: 'Faith Inspiration',
      description: 'Weekly devotionals, encouraging messages, and community testimonials'
    }
  ];

  const socialPlatforms = [
    { icon: Instagram, name: 'Instagram', handle: '@knowncon', color: '#E4405F' },
    { icon: Youtube, name: 'YouTube', handle: 'KNOWN CON', color: '#FF0000' },
    { icon: Facebook, name: 'Facebook', handle: '/knownconference', color: '#1877F2' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero */}
      <div className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1920&h=1080&fit=crop')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3B]/90 via-[#0B1B3B]/80 to-[#0B1B3B]/70" />
        
        <div className="relative z-10 text-center text-white px-4 max-w-5xl">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Stay <span className="text-[#CFA64B]">Connected</span>
          </h1>
          <p className="text-xl md:text-2xl mb-10 text-gray-200">
            Join the KNOWN CON community — receive updates, stories, and inspiration.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-bold px-8 py-6 text-lg">
              Join Group Chat
            </Button>
            <Button variant="outline" className="bg-white/10 hover:bg-white/20 text-white border-white px-8 py-6 text-lg">
              Subscribe to Newsletter
            </Button>
          </div>
        </div>
      </div>

      {/* Community Chat */}
      <div className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-6">Community Chat</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Join our vibrant community chat where attendees connect before, during, and after KNOWN CON. 
                Share experiences, ask questions, find meetup partners, and build lasting friendships.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Available on Discord and WhatsApp — choose the platform that works best for you and 
                jump into conversations happening right now.
              </p>
              <Button className="bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-bold px-8 py-4 text-lg">
                <MessageCircle className="w-5 h-5 mr-2" />
                Join the Chat
              </Button>
            </div>
            <div className="relative h-96 rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1573164713988-8665fc963095?w=800&h=600&fit=crop" 
                alt="Community" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Newsletter Signup */}
      <div className="py-20" style={{ backgroundColor: '#FFF9E9' }}>
        <div className="max-w-[800px] mx-auto px-6">
          <div className="text-center mb-12">
            <Mail className="w-16 h-16 text-[#CFA64B] mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Newsletter Signup</h2>
            <p className="text-xl text-gray-600">Get the latest updates delivered straight to your inbox</p>
          </div>

          <Card className="border-none shadow-xl">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-[#0B1B3B] mb-2">Name *</label>
                  <input
                    type="text"
                    required
                    value={newsletterData.name}
                    onChange={(e) => setNewsletterData({...newsletterData, name: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#CFA64B] focus:outline-none"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#0B1B3B] mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={newsletterData.email}
                    onChange={(e) => setNewsletterData({...newsletterData, email: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#CFA64B] focus:outline-none"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#0B1B3B] mb-2">Interests</label>
                  <select
                    value={newsletterData.interests}
                    onChange={(e) => setNewsletterData({...newsletterData, interests: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#CFA64B] focus:outline-none"
                  >
                    <option value="">Select your interests</option>
                    <option value="worship">Worship & Music</option>
                    <option value="leadership">Leadership Development</option>
                    <option value="community">Community Building</option>
                    <option value="all">All Updates</option>
                  </select>
                </div>
                <Button 
                  type="submit"
                  className="w-full bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-bold py-6 text-lg"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Subscribe Now
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Benefits */}
      <div className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Benefits of Staying Connected</h2>
            <p className="text-xl text-gray-600">More than just updates — it's a lifestyle</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <Card key={idx} className="border-2 border-gray-200 hover:border-[#CFA64B] hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-8 text-center">
                    <div className="w-20 h-20 mx-auto mb-6 bg-[#CFA64B]/10 rounded-full flex items-center justify-center">
                      <Icon className="w-10 h-10 text-[#CFA64B]" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#0B1B3B] mb-4">{benefit.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Social Follow Bar */}
      <div className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Follow Us on Social</h2>
            <p className="text-xl text-gray-600">Stay connected with our daily posts and live updates</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {socialPlatforms.map((platform, idx) => {
              const Icon = platform.icon;
              return (
                <a
                  key={idx}
                  href="#"
                  className="block group"
                >
                  <Card className="border-2 border-gray-200 hover:shadow-xl transition-all duration-300 hover:border-[#CFA64B]">
                    <CardContent className="p-10 text-center">
                      <div 
                        className="w-32 h-32 mx-auto mb-6 rounded-full flex items-center justify-center transition-all group-hover:scale-110"
                        style={{ backgroundColor: platform.color }}
                      >
                        <Icon className="w-16 h-16 text-white fill-white" strokeWidth={0} fill="currentColor" />
                      </div>
                      <h3 className="text-2xl font-bold text-[#0B1B3B] mb-2 group-hover:text-[#CFA64B] transition-colors">{platform.name}</h3>
                      <p className="text-gray-600 text-lg">{platform.handle}</p>
                    </CardContent>
                  </Card>
                </a>
              );
            })}
          </div>
        </div>
      </div>

      {/* Closing CTA */}
      <div className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3B] to-[#CFA64B]" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="text-4xl md:text-6xl font-bold mb-10">
            Together we're building a movement of peace and purpose.
          </h2>
          <Button className="bg-white text-[#0B1B3B] hover:bg-gray-100 font-bold px-10 py-6 text-lg">
            Subscribe Now
          </Button>
        </div>
      </div>

      {/* Quick Links */}
      <div className="py-6 bg-gray-50 border-t border-gray-200">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <a href="/attendee-guide" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">Attendee Guide</a>
            <span className="text-gray-400">|</span>
            <a href="/faqs" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">FAQs</a>
            <span className="text-gray-400">|</span>
            <a href="#contact" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">Contact Support</a>
          </div>
        </div>
      </div>

      <footer className="bg-[#0f172a] text-white py-12">
        <div className="max-w-[1440px] mx-auto px-6 text-center">
          <p className="text-gray-400 text-sm">© 2025 KNOWN CON. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default StayConnectedPage;